import { FC, useMemo, ReactNode, useState, useEffect, useRef } from 'react';
/* Icons start */
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandIcon from '@mui/icons-material/Expand';
import FlipIcon from '@mui/icons-material/Flip';
import EventIcon from '@mui/icons-material/Event';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
/* Icons end */
/* Cells start */
import { QuantityEditCell } from './gridItems/cells/quantityEdit.cell.tsx';
import { MinQuantityEditCell } from './gridItems/cells/minQuantityEdit.cell.tsx';
import { MaxQuantityEditCell } from './gridItems/cells/maxQuantityEdit.cell.tsx';
import { ChangeInOrderCell } from './gridItems/cells/changeInOrder.cell.tsx';
import { CustomerCell } from './gridItems/cells/customer/customer.cell.tsx';
/* Cells end */
import { IDefaultOrderState, defaultOrderState } from '../../defaultOrder.state.ts';
import { styled, Stack, Box, Button } from '@mui/material';
import { Table } from '../../../../../../../shared/components/table/table.component.tsx';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import { useTranslation } from 'react-i18next';
import { Column } from '../../../../../../../shared/components/table/components/common.components.tsx';
import { FieldsSection, FieldsRow } from '../../../../../../../shared/components/form';
import { IconButton } from '../../../../../../order/common/iconButton/iconButton.component.tsx';
import { WeekPicker } from '../../../../../../../shared/components/datePicker/weekPicker.component.tsx';
import { ToggleActionButton } from '../../../../../../../shared/components/toggleActionButton/toggleActionButton.tsx';
import { formatValue } from '../../../../../../../shared/helpers/utils/utils.helper.ts';
import { storageHelper } from '../../../../../../../shared/helpers/storage';
import { articleTabLoadingService } from '../../../articleTababLoading.service.ts';
import { attachMinMaxToQuantity } from './gridItems/cells/additionalFunctions.ts';
import { CustomerEditCell } from './gridItems/cells/customer/customerEdit.cell.tsx';
import { viewCells } from './gridItems/views/viewCells.ts';
import { viewRows } from './gridItems/views/viewRows.ts';
import { DeliverySplittingPopup } from './popups/deliverySplitting/deliverySplitting.popup.tsx';
import { delay, filter } from 'rxjs';
import { useForm, FormProvider } from 'react-hook-form';
import { ISplittingFormData } from '../../../../../../customer/components/tabs/defaultOrder/defaultOrder.state.ts';
import { articleListState } from '../../../../../states/articleList.state.ts';
import { IDefaultOrderTabOptions } from '../../../../../states/tabState.model.ts';
import { GridCellEditStartReasons } from '@mui/x-data-grid/models/params/gridEditCellParams';
import { useGridApiRef } from '@mui/x-data-grid-premium';

export const DefaultOrderGrid: FC<IDefaultOrderGrid> = ({ data, defaultOrderTabOptions }) => {
  const { customerList = [] } = defaultOrderTabOptions;
  const deliverySplittingPopup = useMemo(() => new DeliverySplittingPopup(), []);
  const { defaultOrderGridData, fromDate, selectedPos } = data;
  const { t } = useTranslation();

  const [lockGrid, setLockGrid] = useState(true);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(false);
  const [cellsLoading, setCellsLoading] = useState<string[]>([]);
  const gridApiRef = useGridApiRef();
  const editCellReason = useRef<GridCellEditStartReasons | undefined>(undefined);
  const saveDefaultOrderGridByArticle = useRef((e: MouseEvent) => {
    if (!(e.target instanceof Element)) return;
    const target = e.target;
    if (!target.closest('.defaultOrder') && !target.closest('[datatype=customerOptions]')) {
      defaultOrderState.pub.saveDefaultOrderGrid();
    }
  });

  const formSplittingMethods = useForm<ISplittingFormData>({
    mode: 'onChange',
    defaultValues: {
      enabledDays: [],
    },
  });

  useEffect(() => {
    if (lockGrid && gridApiRef.current) {
      const editedCells = gridApiRef.current.state.editRows;
      for (const id of Object.keys(editedCells)) {
        for (const field of Object.keys(editedCells[id])) {
          gridApiRef.current.stopCellEditMode({ id, field });
        }
      }
    }
  }, [lockGrid]);

  useEffect(() => {
    const articleListStateSub = articleListState.sub
      .state()
      .pipe(
        filter(
          ({ action }) =>
            action === 'selectArticle' || action === 'filter' || action === 'articleList',
        ),
      )
      .subscribe(() => {
        formSplittingMethods.reset();
      });
    const articleTabLoadingSub = articleTabLoadingService.sub.loading().subscribe((loading) => {
      if (!loading) setLockGrid(true);
    });
    const loadingMainGridSub = defaultOrderState.sub.loadingMainGrid().subscribe(setLoadingGrid);
    const cellsLoadingSub = defaultOrderState.sub.cellsLoading().subscribe(setCellsLoading);
    deliverySplittingPopup.stream
      .state()
      .pipe(delay(0))
      .subscribe(({ action }) => {
        if (action === 'close') {
          document.addEventListener('mouseup', saveDefaultOrderGridByArticle.current);
        }
      });
    document.addEventListener('mouseup', saveDefaultOrderGridByArticle.current);
    return () => {
      articleTabLoadingSub.unsubscribe();
      loadingMainGridSub.unsubscribe();
      cellsLoadingSub.unsubscribe();
      deliverySplittingPopup.stream.unsubscribe();
      articleListStateSub.unsubscribe();
      document.removeEventListener('mouseup', saveDefaultOrderGridByArticle.current);
    };
  }, []);

  const buttonList: IButtonList[] = useMemo(
    () => [
      {
        title: t('article.addNewRecord'),
        icon: <AddIcon />,
        onClick: () => defaultOrderState.pub.addPosition(),
        disabled: lockGrid,
      },
      {
        title: t('article.deleteRecord'),
        icon: <RemoveIcon />,
        onClick: () => defaultOrderState.pub.deletePosition(),
        disabled: lockGrid || !selectedPos,
      },
      {
        title: t('article.orderLimit'),
        icon: <ExpandIcon />,
        active: visibleLimit,
        onClick: () => {
          const timer = setTimeout(() => {
            setVisibleLimit((prevState) => !prevState);
            clearTimeout(timer); // fixes the problem if the cell is in edit mode
          }, 0);
        },
      },
      {
        title: t('article.deliverySplittingConfig'),
        icon: <FlipIcon />,
        disabled: lockGrid || !defaultOrderGridData.length,
        onClick: () => {
          deliverySplittingPopup.stream.emit('open', {
            tableData: structuredClone(defaultOrderGridData.filter((el) => el.customerId)),
          });
          document.removeEventListener('mouseup', saveDefaultOrderGridByArticle.current);
        },
      },
    ],
    [t, lockGrid, visibleLimit, selectedPos, defaultOrderGridData],
  );

  return (
    <Box className='defaultOrder'>
      <FieldsSection title={t('article.defaultOrderItems')} titleBoxSx={{ pt: 0 }} nomt>
        <FieldsRow marginBottom={1} justifyContent='space-between' alignItems='center' spacing={0}>
          <Stack direction='row' spacing={2}>
            {buttonList.map(({ icon, ...props }, i) => (
              <IconButton key={i} color='primary' size='large' {...props}>
                {icon}
              </IconButton>
            ))}
            <ToggleActionButton
              color='primary'
              size='large'
              icon={<EventIcon />}
              title={t('customer.articlesAvailabilityOn')}
            >
              <Box
                sx={{
                  p: 1,
                }}
              >
                <WeekPicker
                  value={fromDate}
                  onBlur={([fromDate]) => {
                    if (fromDate) defaultOrderState.pub.fromDate(fromDate);
                  }}
                  width={320}
                />
              </Box>
            </ToggleActionButton>
          </Stack>
          <Button
            variant='outlined'
            onClick={() => setLockGrid((prevState) => !prevState)}
            startIcon={lockGrid ? <LockIcon /> : <LockOpenIcon />}
          >
            {t('customer.editing')}
          </Button>
        </FieldsRow>
      </FieldsSection>
      <STable
        externalApiRef={gridApiRef}
        initialState={{
          columns: storageHelper.local.getItem('article.defaultOrder.mainGridColumnModel'),
        }}
        loading={loadingGrid}
        data={defaultOrderGridData}
        heightOffset={316}
        isCellEditable={() => !lockGrid}
        emptyTableIndication={{
          icon: <LibraryAddIcon />,
          text: t('order.empty_order_grid'),
        }}
        onChangeColumnModel={(v) =>
          storageHelper.local.setItem('article.defaultOrder.mainGridColumnModel', v)
        }
        hidingColumnModel={{
          generatedModelFn: attachMinMaxToQuantity,
        }}
        onRowClick={(e) => {
          if (selectedPos?.id !== e.id) defaultOrderState.pub.selectPosition(e.row);
        }}
        rowSelectionModel={selectedPos?.id || undefined}
        columnVisibilityModel={{
          group_limit: visibleLimit,
        }}
        rowReordering={!lockGrid}
        onChangeOrderPosition={(v) => {
          defaultOrderState.pub.updateGridData(v as []);
        }}
        onRowsCountChange={(rows, prevRows, apiRef) => {
          if (rows.length > prevRows.length && !lockGrid) {
            const newRow = rows?.[rows.length - 1];
            if (newRow.__reorder__ === t('order.new_position')) {
              apiRef?.current.startCellEditMode({
                id: newRow.id as string,
                field: 'internalOrFullName',
              });
            }
          }
        }}
        onCellEditStart={(params) => {
          editCellReason.current = params.reason;
        }}
        {...(!lockGrid && {
          focusTabProps: { enabled: true, enabledFields: ['canChangeInOrders'] },
        })}
        enableSingleClickEditing={false}
        {...{ viewCells, viewRows }}
      >
        <Column
          sortable={false}
          headerName={t('common.customer_no')}
          field='customerNr'
          width={120}
        />
        <Column
          sortable={false}
          headerName={t('common.name')}
          width={350}
          field='internalOrFullName'
          renderCell={(params) => <CustomerCell {...{ params, loading: cellsLoading }} />}
          renderEditCell={(params) => (
            <CustomerEditCell
              editCellReason={editCellReason.current}
              {...{ params, customerList }}
            />
          )}
          editable
        />
        <Column
          sortable={false}
          headerName={t('common.mon')}
          field='mondaySettings.quantity'
          valueGetter={(_, row) => row.mondaySettings.quantity}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <QuantityEditCell {...{ params }} />}
          width={90}
          headerAlign='right'
          align='right'
          editable
        />
        <Column
          sortable={false}
          field='limit.mondaySettings.quantityMin'
          disableReorder={true}
          valueGetter={(_, row) => row.mondaySettings.quantityMin}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MinQuantityEditCell {...{ params }} />}
          headerName={t('common.min')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          field='limit.mondaySettings.quantityMax'
          disableReorder={true}
          valueGetter={(_, row) => row.mondaySettings.quantityMax}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MaxQuantityEditCell {...{ params }} />}
          headerName={t('common.max')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          headerName={t('common.tue')}
          field='tuesdaySettings.quantity'
          valueGetter={(_, row) => row.tuesdaySettings.quantity}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <QuantityEditCell {...{ params }} />}
          width={90}
          headerAlign='right'
          align='right'
          editable
        />
        <Column
          sortable={false}
          field='limit.tuesdaySettings.quantityMin'
          disableReorder={true}
          valueGetter={(_, row) => row.tuesdaySettings.quantityMin}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MinQuantityEditCell {...{ params }} />}
          headerName={t('common.min')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          field='limit.tuesdaySettings.quantityMax'
          disableReorder={true}
          valueGetter={(_, row) => row.tuesdaySettings.quantityMax}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MaxQuantityEditCell {...{ params }} />}
          headerName={t('common.max')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          headerName={t('common.wed')}
          field='wednesdaySettings.quantity'
          valueGetter={(_, row) => row.wednesdaySettings.quantity}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <QuantityEditCell {...{ params }} />}
          width={90}
          headerAlign='right'
          align='right'
          editable
        />
        <Column
          sortable={false}
          field='limit.wednesdaySettings.quantityMin'
          disableReorder={true}
          valueGetter={(_, row) => row.wednesdaySettings.quantityMin}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MinQuantityEditCell {...{ params }} />}
          headerName={t('common.min')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          field='limit.wednesdaySettings.quantityMax'
          disableReorder={true}
          valueGetter={(_, row) => row.wednesdaySettings.quantityMax}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MaxQuantityEditCell {...{ params }} />}
          headerName={t('common.max')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          headerName={t('common.thu')}
          renderEditCell={(params) => <QuantityEditCell {...{ params }} />}
          field='thursdaySettings.quantity'
          valueGetter={(_, row) => row.thursdaySettings.quantity}
          valueFormatter={(value) => formatValue(value)}
          width={90}
          headerAlign='right'
          align='right'
          editable
        />
        <Column
          sortable={false}
          field='limit.thursdaySettings.quantityMin'
          disableReorder={true}
          valueGetter={(_, row) => row.thursdaySettings.quantityMin}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MinQuantityEditCell {...{ params }} />}
          headerName={t('common.min')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          field='limit.thursdaySettings.quantityMax'
          disableReorder={true}
          valueGetter={(_, row) => row.thursdaySettings.quantityMax}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MaxQuantityEditCell {...{ params }} />}
          headerName={t('common.max')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          headerName={t('common.fri')}
          renderEditCell={(params) => <QuantityEditCell {...{ params }} />}
          field='fridaySettings.quantity'
          valueGetter={(_, row) => row.fridaySettings.quantity}
          valueFormatter={(value) => formatValue(value)}
          width={90}
          headerAlign='right'
          align='right'
          editable
        />
        <Column
          sortable={false}
          field='limit.fridaySettings.quantityMin'
          disableReorder={true}
          valueGetter={(_, row) => row.fridaySettings.quantityMin}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MinQuantityEditCell {...{ params }} />}
          headerName={t('common.min')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          field='limit.fridaySettings.quantityMax'
          disableReorder={true}
          valueGetter={(_, row) => row.fridaySettings.quantityMax}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MaxQuantityEditCell {...{ params }} />}
          headerName={t('common.max')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          headerName={t('common.sat')}
          renderEditCell={(params) => <QuantityEditCell {...{ params }} />}
          field='saturdaySettings.quantity'
          valueGetter={(_, row) => row.saturdaySettings.quantity}
          valueFormatter={(value) => formatValue(value)}
          width={90}
          headerAlign='right'
          align='right'
          editable
        />
        <Column
          sortable={false}
          field='limit.saturdaySettings.quantityMin'
          disableReorder={true}
          valueGetter={(_, row) => row.saturdaySettings.quantityMin}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MinQuantityEditCell {...{ params }} />}
          headerName={t('common.min')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          field='limit.saturdaySettings.quantityMax'
          disableReorder={true}
          valueGetter={(_, row) => row.saturdaySettings.quantityMax}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MaxQuantityEditCell {...{ params }} />}
          headerName={t('common.max')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          headerName={t('common.sun')}
          renderEditCell={(params) => <QuantityEditCell {...{ params }} />}
          field='sundaySettings.quantity'
          valueGetter={(_, row) => row.sundaySettings.quantity}
          valueFormatter={(value) => formatValue(value)}
          width={90}
          headerAlign='right'
          align='right'
          editable
        />
        <Column
          sortable={false}
          field='limit.sundaySettings.quantityMin'
          disableReorder={true}
          valueGetter={(_, row) => row.sundaySettings.quantityMin}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MinQuantityEditCell {...{ params }} />}
          headerName={t('common.min')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          field='limit.sundaySettings.quantityMax'
          disableReorder={true}
          valueGetter={(_, row) => row.sundaySettings.quantityMax}
          valueFormatter={(value) => formatValue(value)}
          renderEditCell={(params) => <MaxQuantityEditCell {...{ params }} />}
          headerName={t('common.max')}
          align='right'
          headerAlign='right'
          width={80}
          editable
        />
        <Column
          sortable={false}
          headerName={t('article.changesProhibited')}
          field='canChangeInOrders'
          width={170}
          headerAlign='center'
          align='center'
          renderCell={(params) => <ChangeInOrderCell {...{ params, disabled: lockGrid }} />}
        />
      </STable>
      <FormProvider {...formSplittingMethods}>
        <deliverySplittingPopup.Component />
      </FormProvider>
    </Box>
  );
};

const STable = styled(Table)(({ theme }) => ({
  '& .MuiDataGrid-columnHeaders [data-field^="limit"]': {
    backgroundColor: theme.palette.grey['200'],
  },
}));

interface IDefaultOrderGrid {
  data: IDefaultOrderState;
  defaultOrderTabOptions: IDefaultOrderTabOptions;
}

interface IButtonList {
  title: string;
  icon: ReactNode;
  disabled?: boolean;
  active?: boolean;
  onClick?: () => void;
}
